import {memo, useCallback, useState} from 'react'
import {BranchName, Dialog, type DialogButtonProps, Flash, Link, NavList, Spinner} from '@primer/react'
import {GitBranchIcon, StackIcon, TrashIcon} from '@primer/octicons-react'
import {useQueryClient} from '@github-ui/react-query'

import {usePreviewStackData, previewStackQueryKey} from '../../page-data/loaders/use-preview-stack-data'
import type {PreviewStackData, PreviewStackPull} from '../../page-data/payloads/preview-stack'
import {useHeaderPageDataQueryKey} from '../../page-data/loaders/use-header-page-data'
import {useCreateStackOrAppendMutation} from '../../mutations/use-create-stack-or-append-mutation'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {PullRequestState} from '../../types'
import {StateIcon} from '../StateIcon'
import type {StackCreatedInfo} from './StackCreatedBanner'
import styles from './PreviewStackDialog.module.css'

export type PreviewStackDialogProps = {
  currentPullNumber: number
  onClose: () => void
  onStackCreated: (info: StackCreatedInfo) => void
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
}

export function PreviewStackDialog({
  currentPullNumber,
  onClose,
  onStackCreated,
  returnFocusRef,
}: PreviewStackDialogProps) {
  const {data, isPending} = usePreviewStackData()
  const [originalData, setOriginalData] = useState<PreviewStackData | null>(null)
  const [pulls, setPulls] = useState<PreviewStackPull[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const {mutate: createStackOrAppend, isPending: isSubmitting} = useCreateStackOrAppendMutation()
  const queryClient = useQueryClient()
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const previewStackURL = usePageDataUrl(PageData.previewStack)

  if (data && data !== originalData) {
    setOriginalData(data)
    setPulls(data)
  }

  const isModified = originalData !== null && pulls.length !== originalData.length
  const hasPulls = pulls.length > 0
  // The tail (bottom) pull carries the existing stack id when this preview
  // walked down into one. Pulls are top-first in display order, so the bottom
  // is the last element.
  const existingStackId = hasPulls ? (pulls[pulls.length - 1]?.stackId ?? null) : null
  const isExtendingExistingStack = existingStackId != null
  const createStackLabel = isExtendingExistingStack ? 'Add to stack' : 'Create stack'

  const handleRemoveTop = useCallback(() => {
    setPulls(prev => prev.slice(1))
  }, [])

  const handleReset = () => {
    if (originalData) setPulls(originalData)
  }

  const handleSubmit = () => {
    if (!hasPulls) return
    setSubmitError(null)
    // Backend expects position ASC (bottom-first); display order is top-first,
    // so we iterate in reverse. When appending, skip pulls already in a stack
    // -- the controller loads the existing membership from `stackId` itself.
    const pullRequestIds: number[] = []
    for (let i = pulls.length - 1; i >= 0; i--) {
      const pull = pulls[i]
      if (!pull) continue
      if (isExtendingExistingStack && pull.stackId != null) continue
      pullRequestIds.push(pull.id)
    }
    if (pullRequestIds.length === 0) return
    createStackOrAppend(
      {
        pullRequestIds,
        stackId: existingStackId,
      },
      {
        onSuccess: async response => {
          try {
            await queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true})
            queryClient.invalidateQueries({queryKey: previewStackQueryKey(previewStackURL)})
            onStackCreated({stackNumber: response.stackNumber, appended: isExtendingExistingStack})
            onClose()
          } catch {
            setSubmitError('Something unexpected happened. Please refresh the page.')
          }
        },
        onError: error => {
          setSubmitError(error.message ?? 'Failed to create stack.')
        },
      },
    )
  }

  const footerButtons: DialogButtonProps[] = []
  if (isModified) {
    footerButtons.push({
      content: 'Reset stack',
      onClick: handleReset,
      disabled: isSubmitting,
      title: 'Reset to the original proposed stack',
    })
  }
  footerButtons.push({content: 'Cancel', onClick: onClose, disabled: isSubmitting})
  footerButtons.push({
    content: createStackLabel,
    buttonType: 'primary',
    onClick: handleSubmit,
    disabled: isPending || isSubmitting || !hasPulls,
  })

  return (
    <Dialog
      title="Preview stack"
      returnFocusRef={returnFocusRef}
      onClose={onClose}
      footerButtons={footerButtons}
      height="small"
    >
      <p className="mb-2">
        This pull request can be stacked with other pull requests. Review the proposed stack and create it when you’re
        ready.{' '}
        <Link inline href="https://github.github.com/gh-stack/">
          Learn more about pull request stacks
        </Link>
      </p>
      {submitError ? (
        <Flash variant="danger" className="mb-2">
          {submitError}
        </Flash>
      ) : null}
      <PreviewStackList
        pulls={pulls}
        isPending={isPending}
        onRemoveTop={handleRemoveTop}
        disableRemove={isSubmitting}
        currentPullNumber={currentPullNumber}
      />
    </Dialog>
  )
}

type PreviewStackListProps = {
  pulls: PreviewStackPull[]
  isPending: boolean
  onRemoveTop: () => void
  disableRemove: boolean
  currentPullNumber: number
}

function PreviewStackList({pulls, isPending, onRemoveTop, disableRemove, currentPullNumber}: PreviewStackListProps) {
  const hasPulls = !isPending && pulls.length > 0
  const lastIndex = pulls.length - 1
  // Pulls already in an existing stack collectively count as one stackable
  // unit, since removing the top standalone pull would still leave a valid
  // 2-pull stack (the existing one).
  const unstackedCount = pulls.filter(pull => pull.stackId == null).length
  const hasExistingStack = pulls.some(pull => pull.stackId != null)
  const stackablePullCount = unstackedCount + (hasExistingStack ? 1 : 0)
  const canRemoveTop = stackablePullCount > 2

  return (
    <div
      role="region"
      className={styles.stackContainer}
      aria-label="Proposed stack of pull requests"
      aria-busy={isPending}
    >
      {isPending ? (
        <Spinner srText="Loading proposed stack" />
      ) : hasPulls ? (
        <NavList className={styles.stackNavList}>
          {pulls.map((pull, index) => {
            const onRemove = canRemoveTop && index === 0 ? onRemoveTop : undefined
            const isCurrent = pull.number === currentPullNumber
            return (
              <PreviewStackPullItem
                key={pull.number}
                pull={pull}
                isCurrent={isCurrent}
                isTail={index === lastIndex}
                onRemove={onRemove}
                disableRemove={disableRemove}
              />
            )
          })}
        </NavList>
      ) : null}
    </div>
  )
}

const PreviewStackPullItem = memo(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name for readable stack traces
  function PreviewStackPullItem({
    pull,
    isCurrent,
    isTail,
    onRemove,
    disableRemove,
  }: {
    pull: PreviewStackPull
    isCurrent: boolean
    isTail: boolean
    onRemove?: () => void
    disableRemove: boolean
  }) {
    const destinationBranch = isTail ? pull.baseBranch : null

    return (
      <>
        <NavList.Item href={pull.url} aria-current={isCurrent ? 'page' : undefined}>
          <NavList.LeadingVisual>
            {pull.stackId != null ? <StackIcon /> : <StateIcon state={pull.state ?? PullRequestState.Open} />}
          </NavList.LeadingVisual>
          <div className={styles.prItemContent}>
            <span className={styles.prTitle}>{pull.title}</span>
            <span className={styles.prMeta}>
              <span className="text-normal">#{pull.number}</span>
              <span className={styles.prBranch}>
                <BranchName as="span" className={styles.prBranchName}>
                  {pull.headBranch}
                </BranchName>
              </span>
            </span>
          </div>
          {onRemove ? (
            <NavList.TrailingAction
              as="button"
              icon={TrashIcon}
              label={
                isCurrent ? "You can't remove the currently viewed pull request" : `Remove ${pull.title} from stack`
              }
              disabled={disableRemove || isCurrent}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault()
                event.stopPropagation()
                onRemove()
              }}
            />
          ) : null}
        </NavList.Item>
        {isTail && destinationBranch ? (
          <li className={styles.branchItem}>
            <span className={styles.branchItemContent}>
              <GitBranchIcon className="fgColor-muted" />
              <BranchName as="span">{destinationBranch}</BranchName>
            </span>
          </li>
        ) : null}
      </>
    )
  },
)
